import _ from 'lodash';
import { ReactNode, createContext, useContext, useState, useEffect } from 'react';

import { type FunnelPage_Type, FooterLinks } from 'constant';
import { encodeId } from 'utils/hashId';
import { getRateDataByCountry } from 'utils/fx';
import { parsePrice } from 'utils/price';
import { buildFunnelPath, buildFunnelPageUrl } from 'utils/url';

import { buildFonts, type BuiltFonts } from 'utils/font';
import type { Stores, Offers, FunnelVariants, PageTheme } from 'queries/graphql';

const FunnelDataContext = createContext(null);

const FunnelDataProvider = ({
  funnelData: initialFunnelData,
  pagePath,
  children,
}: {
  funnelData: Record<string, any>;
  pagePath?: string;
  children: ReactNode;
}) => {
  const [funnelData, setFunnelData] = useState(initialFunnelData);
  const [currentPath, setCurrentPath] = useState(pagePath);

  // Reset state for new path to avoid upsell pages maintaining initial funnel data from the first upsell.
  // This may not occur before the first render of other components; they may see
  // the previous page's funnelData for initial render before this updates and causes
  // another render.
  useEffect(() => {
    if (pagePath !== currentPath) {
      setFunnelData(initialFunnelData);
      setCurrentPath(pagePath);
    }
  }, [currentPath, pagePath, initialFunnelData]);

  return (
    <FunnelDataContext.Provider value={{ ...funnelData }}>{children}</FunnelDataContext.Provider>
  );
};

export async function buildGenericFunnelData(
  funnelVariant: FunnelVariants,
  funnelPageType?: FunnelPage_Type,
  path?: string,
  theme?: PageTheme
): Promise<FunnelData> {
  const funnel = funnelVariant.funnel;
  const brand = funnel.brand;
  const store = brand.stores[0];

  const {
    ga4MeasurementId,
    googleTagManagerId,
    googleTagManagerAuthCode,
    googleTagManagerPreview,
    klaviyoPublicKey,
    externalName,
    rootDomain,
    supportEmail,
  } = brand;

  const canonicalUrl =
    path && funnelPageType
      ? buildFunnelPageUrl({
          rootDomain,
          funnelPage: {
            // @lkuich - I don't think type is actually used anywhere other than routing
            type: funnelPageType,
            path,
          },
          fid: funnelVariant?.id,
        })
      : null;

  const useLocalCurrency = funnelVariant.useLocalCurrency;
  const currency = store?.currency ?? 'USD';
  let fxRates = null;

  if (useLocalCurrency) {
    try {
      fxRates = await getRateDataByCountry(currency);
    } catch (error) {
      console.error('Failed to fetch rates from Fixer API.', error);
    }
  }

  const isPaypalSupported = brand.stores.every(
    _store => _store.paypalOnboardingStatus === 'Completed'
  );

  const isAfterpaySupported = brand.stores.every(_store => {
    const supported = _store.afterpaySupported;
    const validPrices = _store.offers.every(offer => {
      const validPrice = parsePrice(offer?.price) >= 1 && parsePrice(offer?.price) <= 2000;
      return supported && validPrice;
    });
    return supported && validPrices;
  });

  const encodedFunnelVariantId = encodeId(funnelVariant.id);

  const fonts: BuiltFonts = buildFonts(theme);

  return convertUndefinedToNull({
    // @lkuich - Naming between funnel and funnelVariantId is inconsistent
    funnelId: encodedFunnelVariantId,
    funnelName: funnelVariant.name,
    apiBaseUrl: process.env.NEXT_PUBLIC_CHECKOUT_API,
    rootDomain,
    supportEmail,
    brandExternalName: externalName,
    ga4MeasurementId,
    googleTagManagerId,
    googleTagManagerAuthCode,
    googleTagManagerPreview,
    klaviyoPublicKey,
    canonicalUrl,
    store,
    useLocalCurrency,
    fxRates,
    currency,
    isPaypalSupported,
    isAfterpaySupported,
    links: {
      privacyPolicy: buildFunnelPath(FooterLinks.PRIVACY_POLICY, encodedFunnelVariantId),
      termsAndConditions: buildFunnelPath(FooterLinks.TERMS_AND_CONDITIONS, encodedFunnelVariantId),
      refundPolicy: buildFunnelPath(FooterLinks.REFUND_POLICY, encodedFunnelVariantId),
      contactUs: buildFunnelPath(FooterLinks.CONTACT_US, encodedFunnelVariantId),
    },
    ...fonts,
  });
}

export type FunnelData = {
  funnelId: string;
  funnelName: string;
  apiBaseUrl: string;
  rootDomain: string;
  supportEmail: string;
  brandExternalName: string;
  ga4MeasurementId: string;
  googleTagManagerId: string;
  googleTagManagerAuthCode: string;
  googleTagManagerPreview: string;
  klaviyoPublicKey: string;
  canonicalUrl: string;
  merchantName?: string;
  links: {
    privacyPolicy: string;
    termsAndConditions: string;
    refundPolicy: string;
    contactUs: string;
  };
  isPaypalSupported: boolean;
  isAfterpaySupported: boolean;
  useLocalCurrency: boolean;
  currency: string;
  fxRates: any;
  store: Stores;
} & BuiltFonts;

export type SalesFunnelData = FunnelData & {
  offersConfig: OfferConfig[];
  upsellUrl: string;
  checkoutPath: string;
};

export type UpsellFunnelData = FunnelData & {
  offersConfig: OfferConfig[];
  cartItems: { offerId: string; quantity: number }[];
  cartData: any;
  upsellUrl: string;
  downsellUrl: string;
  defaultPath: string;
  checkoutPath: string;
};

export type PresellFunnelData = FunnelData & {
  salesPath: string;
};

export type CheckoutFunnelData = FunnelData & {
  upsellUrl: string;
  offersConfig: OfferConfig[];
  useLocalCurrency: boolean;
  policyContent: {
    [x: string]: {
      __typename?: 'PolicyPage';
      heading?: string | null;
      text?: string | null;
      type?: any | null;
    };
  };
};

export type PolicyFunnelData = FunnelData & {
  offersConfig: OfferConfig[];
};

export type FunnelDataWithOfferConfig = FunnelData & {
  offersConfig: OfferConfig[];
};

function convertUndefinedToNull(value) {
  if (_.isArray(value)) {
    return value.map(item => convertUndefinedToNull(item));
  } else if (_.isObject(value)) {
    return _.mapValues(value, v => convertUndefinedToNull(v));
  } else {
    return value === undefined ? null : value;
  }
}

export type OfferConfig = Omit<Offers, 'id'> & {
  id: string;
  fid: string;
  // This is null coming from server props, populated client-side
  quantity?: number;
  productUrl: string;
  image: string;
  store: Stores;
};

export function buildOfferConfig<
  OfferType extends Partial<Offers>,
  FunnelVariantType extends FunnelVariants
>(
  offer: OfferType,
  funnelVariant: FunnelVariantType,
  type: FunnelPage_Type,
  path?: string
): OfferConfig {
  let result = {} as OfferConfig;

  if (funnelVariant) {
    const fid = encodeId(funnelVariant.id);
    result.fid = fid;

    const productUrl = path
      ? buildFunnelPageUrl({
          rootDomain: funnelVariant.funnel.brand.rootDomain,
          funnelPage: {
            type,
            path,
          },
          fid,
        })
      : null;

    if (productUrl) {
      result.productUrl = productUrl;
    }
  } else {
    console.error('Missing funnelVariant', {
      offer,
      funnelVariant,
      path,
    });
  }

  if (offer) {
    result = {
      ...result,
      ...offer,
      id: offer.hashedId,
      image: offer.product?.featuredImage ? offer.product.featuredImage : offer.image,
    };
  } else {
    console.error('Missing offer config', {
      offer,
      funnelVariant,
      path,
    });
  }

  return result;
}

function useFunnelData<
  T extends
    | SalesFunnelData
    | UpsellFunnelData
    | CheckoutFunnelData
    | PolicyFunnelData
    | PresellFunnelData
>(): T {
  const context = useContext(FunnelDataContext);
  if (context === undefined) {
    throw new Error('useFunnelData must be used within a FunnelDataProvider');
  }

  return context;
}

export { FunnelDataProvider, useFunnelData };
